import { getExtensionValue, MedplumClient, MedplumRequestOptions } from "@medplum/core";
import {
  Organization,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import {
  COVERAGE_GROUP_LINK_ID,
  VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS,
} from "@/billing/constants/insurance-verification";
import {
  buildInsuranceSnapshotMetaTags,
  getInsuranceVerificationSnapshot,
  type InsuranceVerificationSnapshot,
} from "@/billing/utils/insurance-verification-snapshot";
import { INSURANCE_VERIFICATION_IDENTIFIER } from "@/fhir/constants/identifier";
import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { resourceHasOrganizationCompartment, setAccounts } from "@/utils/accounts";
import {
  getActiveOrganizationId,
  getActiveOrganizationReference,
  joinReferenceString,
  removeExtensions,
  setExtensionValue,
} from "@/utils/medplum";
import { getMedplumErrorStatus } from "@/utils/medplum-http-error";
import { ifMatchVersionOptions } from "@/utils/medplum-if-match";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

export type InsuranceVerificationTier = "primary" | "secondary" | "tertiary";

const VERIFICATION_IDENTIFIER_VALUE = Questionnaires.InsuranceBenefitsVerification;

/**
 * Patches the coverage group so repeat choice answers (e.g. providerTypeAndSettingCoverage)
 * have correct answer arrays. getQuestionnaireResponseFromFormValues does not set answers
 * for simple choice repeats when given { "0": "A", "1": "B" }.
 */
function patchCoverageRepeatChoiceAnswers(
  response: QuestionnaireResponse,
  values: Record<string, unknown>,
  linkId: string,
): QuestionnaireResponse {
  const raw = values[linkId];
  const strings: string[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && !("code" in raw)
      ? (Object.values(raw) as string[])
      : [];
  if (strings.length === 0) return response;

  const coverageItem = response.item?.find((i) => i.linkId === COVERAGE_GROUP_LINK_ID);
  if (!coverageItem?.item) return response;

  const otherItems = coverageItem.item.filter((i) => i.linkId !== linkId);
  const repeatItems: QuestionnaireResponseItem[] = strings.map((value) => ({
    linkId,
    answer: [{ valueString: value }],
  }));
  const patchedCoverage: QuestionnaireResponseItem = {
    ...coverageItem,
    item: [...otherItems, ...repeatItems],
  };
  const item = (response.item ?? []).map((i) =>
    i.linkId === COVERAGE_GROUP_LINK_ID ? patchedCoverage : i,
  );
  return { ...response, item };
}

export async function fetchVerificationQuestionnaire(
  medplum: MedplumClient,
): Promise<Questionnaire | undefined> {
  let questionnaire = await medplum.searchOne("Questionnaire", {
    url: VERIFICATION_IDENTIFIER_VALUE,
    status: "active",
    _sort: "-_lastUpdated",
    _compartment: getActiveOrganizationReference(),
  });
  if (!questionnaire) {
    questionnaire = await medplum.searchOne("Questionnaire", {
      url: VERIFICATION_IDENTIFIER_VALUE,
      status: "active",
      _sort: "-_lastUpdated",
    });
  }
  return questionnaire;
}

/**
 * Fetches all insurance verification questionnaire responses for a client.
 * Each response has an extension indicating its tier (primary, secondary, tertiary).
 * @param options Request options for the search, such as `{ cache: "no-cache" }`.
 */
export async function fetchVerificationResponsesForClient(
  medplum: MedplumClient,
  clientId: string,
  options?: MedplumRequestOptions,
): Promise<QuestionnaireResponse[]> {
  const bundle = await medplum.searchResources(
    "QuestionnaireResponse",
    {
      patient: joinReferenceString("Patient", clientId),
      identifier: VERIFICATION_IDENTIFIER_VALUE,
      _sort: "-_lastUpdated",
    },
    options,
  );
  return bundle ?? [];
}

/** FHIR QuestionnaireResponse status for a response that is abandoned (e.g. insurance changed). */
const VERIFICATION_STATUS_STOPPED = "stopped";

/**
 * Returns the verification response for the given tier that is still active (not stopped).
 * When insurance changes we set the old response to "stopped" and create a new one for the new insurance.
 */
export function getVerificationResponseForTier(
  responses: QuestionnaireResponse[],
  tier: InsuranceVerificationTier,
): QuestionnaireResponse | undefined {
  const forTier = responses.filter(
    (qr) => getExtensionValue(qr, Extensions.InsuranceVerificationTier) === tier,
  );
  const active = forTier.filter((qr) => qr.status !== VERIFICATION_STATUS_STOPPED);
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  const byDate = [...active].sort(
    (a, b) =>
      new Date(b.meta?.lastUpdated ?? 0).getTime() - new Date(a.meta?.lastUpdated ?? 0).getTime(),
  );
  return byDate[0];
}

/**
 * Returns all verification responses for the given tier (active and stopped).
 * Used for version history so we can show coverage from every response.
 */
export function getVerificationResponsesForTier(
  responses: QuestionnaireResponse[],
  tier: InsuranceVerificationTier,
): QuestionnaireResponse[] {
  return responses.filter(
    (qr) => getExtensionValue(qr, Extensions.InsuranceVerificationTier) === tier,
  );
}

/**
 * Reads the insurance snapshot from a verification questionnaire response, if present.
 * Used to match coverage versions to intake versions by insurance id, company, and group number.
 */
export function getInsuranceSnapshotFromVerificationResponse(
  response: QuestionnaireResponse | undefined,
): InsuranceSnapshot | undefined {
  return getInsuranceVerificationSnapshot(response);
}

/**
 * Fetches the single verification response for a client and tier.
 * @param options Request options for the search, such as `{ cache: "no-cache" }`.
 */
export async function fetchVerificationResponseForTier(
  medplum: MedplumClient,
  clientId: string,
  tier: InsuranceVerificationTier,
  options?: MedplumRequestOptions,
): Promise<QuestionnaireResponse | undefined> {
  const all = await fetchVerificationResponsesForClient(medplum, clientId, options);
  return getVerificationResponseForTier(all, tier);
}

/**
 * Returns a copy of the response with QuestionnaireResponseNamedVersion bumped (v0→v1, v1→v2, …).
 * Used so coverage verification responses maintain version history like the intake.
 */
function withBumpedNamedVersion(response: QuestionnaireResponse): QuestionnaireResponse {
  const version = getExtensionValue(
    response,
    Extensions.QuestionnaireResponseNamedVersion,
  )?.toString();
  const currentVersion = version?.slice(1);
  const nextVersion = isNaN(Number(currentVersion)) ? 1 : Number(currentVersion) + 1;
  const extension = setExtensionValue(
    response.extension ?? [],
    Extensions.QuestionnaireResponseNamedVersion,
    { valueString: `v${nextVersion}` },
  );
  return { ...response, extension };
}

/**
 * If the client has an active verification response for the given tier, sets its status to "stopped"
 * and updates the resource. Call this when insurance id, name, or group number change (Verify
 * Insurance step 2 or intake edit). The next time the user verifies, a new response will be created.
 * The response is read past the request cache and written with `If-Match` on that version, so a
 * response someone else saved in between is refused instead of overwritten.
 */
export async function stopVerificationResponseForTier(
  medplum: MedplumClient,
  clientId: string,
  tier: InsuranceVerificationTier,
): Promise<QuestionnaireResponse | undefined> {
  const response = await fetchVerificationResponseForTier(medplum, clientId, tier, {
    cache: "no-cache",
  });
  if (!response?.id) return undefined;
  return medplum.updateResource(
    { ...response, status: VERIFICATION_STATUS_STOPPED },
    ifMatchVersionOptions(response),
  );
}

export type InsuranceSnapshot = InsuranceVerificationSnapshot & {
  /** The Organization picked in Step 2; `Organization/custom` for a typed insurer. */
  insurancePayer?: Reference<Organization>;
};

/**
 * Creates or updates the insurance benefits verification questionnaire response for a client and tier.
 * Sets the tier extension so the response is associated with primary, secondary, or tertiary insurance.
 * When insuranceSnapshot is provided, stores it on the response so coverage can be matched to intake versions.
 * An update carries `If-Match` with the loaded response's version, so it throws a conflict when
 * someone else saved the response since it was loaded.
 */
export async function saveVerificationResponse({
  medplum,
  questionnaire,
  questionnaireResponse,
  values,
  clientId,
  authorId,
  tier,
  insuranceSnapshot,
}: {
  medplum: MedplumClient;
  questionnaire: Questionnaire;
  questionnaireResponse: QuestionnaireResponse | undefined;
  values: Record<string, unknown>;
  clientId: string;
  authorId: string;
  tier: InsuranceVerificationTier;
  insuranceSnapshot?: InsuranceSnapshot;
}): Promise<QuestionnaireResponse> {
  const subject = (questionnaireResponse?.subject ?? {
    reference: joinReferenceString("Patient", clientId),
  }) as Reference<Patient>;
  const source = (questionnaireResponse?.source ?? {
    reference: joinReferenceString("Practitioner", authorId),
  }) as Reference<Practitioner>;
  const identifier = questionnaireResponse?.identifier ?? {
    system: INSURANCE_VERIFICATION_IDENTIFIER.system,
    value: INSURANCE_VERIFICATION_IDENTIFIER.value,
  };

  let updated = getQuestionnaireResponseFromFormValues(
    questionnaire,
    values,
    subject,
    source,
    questionnaireResponse ?? null,
    identifier,
    COVERAGE_GROUP_LINK_ID,
  );

  updated = patchCoverageRepeatChoiceAnswers(updated, values, "providerTypeAndSettingCoverage");
  // The verifier is whoever saved this check, so a Reverify names the latest user.
  // `source` keeps the user who ran the first check.
  updated = { ...updated, author: { reference: joinReferenceString("Practitioner", authorId) } };

  if (insuranceSnapshot) {
    const existingMeta = updated.meta ?? {};
    const nonSnapshotTags = (existingMeta.tag ?? []).filter(
      (tag) =>
        tag.system !== VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceId &&
        tag.system !== VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceCompany &&
        tag.system !== VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceGroupNumber,
    );
    updated = {
      ...updated,
      meta: {
        ...existingMeta,
        tag: [...nonSnapshotTags, ...buildInsuranceSnapshotMetaTags(insuranceSnapshot)],
      },
    };
  }

  let extension = setExtensionValue(updated.extension, Extensions.InsuranceVerificationTier, {
    valueString: tier,
  });
  if (insuranceSnapshot) {
    const payer = insuranceSnapshot.insurancePayer;
    extension = payer
      ? setExtensionValue(extension, Extensions.InsuranceVerificationPayer, {
          valueReference: { reference: payer.reference, display: payer.display },
        })
      : removeExtensions(extension, [Extensions.InsuranceVerificationPayer]);
  }
  updated = { ...updated, extension };

  // Create and `$qh-set-accounts` are separate writes. Practice AccessPolicies only grant
  // QuestionnaireResponse via `_compartment=%organization`, so an unlinked response is invisible
  // to billers and providers — including the author. Linkage must succeed for Verify to stick.
  const wasCreated = !questionnaireResponse?.id;
  let updatedQuestionnaireResponse: QuestionnaireResponse | undefined;
  if (questionnaireResponse?.id) {
    updated = withBumpedNamedVersion({ ...updated, id: questionnaireResponse.id });
    // A response saved by someone else after it was loaded is refused instead of overwritten.
    updatedQuestionnaireResponse = await medplum.updateResource(
      updated,
      ifMatchVersionOptions(questionnaireResponse),
    );
  } else {
    updatedQuestionnaireResponse = await medplum.createResource(updated);
  }

  try {
    const setAccountsResponse = (await setAccounts(medplum, updatedQuestionnaireResponse, [], {
      extractFromPatient: true,
    })) as { result?: boolean } | undefined;

    if (setAccountsResponse?.result === false) {
      throw new Error("$qh-set-accounts declined to set organization accounts");
    }
  } catch (error) {
    // An update keeps the response's practice compartment, so a Reverify that saved stays readable
    // even when re-linking fails. Report the link failure and return the saved response, so the
    // caller does not show a save error for a verification the Insurance tab already shows.
    if (
      !wasCreated &&
      resourceHasOrganizationCompartment(updatedQuestionnaireResponse, getActiveOrganizationId())
    ) {
      Sentry.captureException(
        new Error("Failed to re-link insurance verification response after update"),
        {
          level: "warning",
          tags: { feature: "insurance-verification", operation: "relink-after-update" },
          extra: {
            questionnaireResponseId: updatedQuestionnaireResponse.id,
            linkStatus: getMedplumErrorStatus(error),
          },
        },
      );
      return updatedQuestionnaireResponse;
    }

    // Compensatory delete: a create that never linked must not linger as an orphan the Insurance
    // tab cannot read. Practice policies scope delete to
    // `_compartment=%organization`, so an unlinked response can refuse deletion the same way it
    // refuses read — report that to Sentry (opaque id only) rather than hiding it under the link
    // error. The original linkage failure is still what the caller surfaces.
    if (wasCreated && updatedQuestionnaireResponse.id) {
      const orphanId = updatedQuestionnaireResponse.id;
      try {
        await medplum.deleteResource("QuestionnaireResponse", orphanId);
      } catch (cleanupError) {
        Sentry.captureException(
          new Error(
            "Failed to delete unlinked insurance verification response after account linkage failed",
          ),
          {
            level: "error",
            tags: { feature: "insurance-verification", operation: "orphan-cleanup" },
            extra: {
              questionnaireResponseId: orphanId,
              cleanupStatus: getMedplumErrorStatus(cleanupError),
              cleanupErrorType:
                cleanupError instanceof Error ? cleanupError.name : typeof cleanupError,
            },
          },
        );
      }
    }
    throw error;
  }

  return updatedQuestionnaireResponse;
}
